import React from "react";
import { Link, useLocation } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ChatWidget from "../components/ChatWidget";
import ClimateClock from "../components/ClimateClock";
import Lightbox from "../components/Lightbox";
import EnquireModal from "../components/EnquireModal";
import "../styles/home.css";
import "../styles/enquire.css";

const TITLES = ["Founder", "Chief Design Officer", "Renaissance Designer", "Badass Boss Bitch", "Warrior of the Light", "Human Being", "Multipotentialite", "Designer of Worlds"];

// Typewriter animation timings (ms)
const TYPE_INTERVAL_MS = 100;
const ERASE_INTERVAL_MS = 60;
const PAUSE_AT_END_MS = 1800;
const HASH_SCROLL_DELAY_MS = 50;

const PROJECTS = [
  { slug: "case-study-be-story", img: "be-story-cover.png", tags: [{ label: "Branding" }, { label: "Behaviour Change" }], title: "The B.E. Story", desc: "A bio-enzyme cleaner brand that asked: what if cleaning your home didn't cost the planet anything? 7 product lines, 5 label iterations, a community refill system on WhatsApp.", impact: "Biodegrades 60–100% in 28 days" },
  { slug: "case-study-ksht", img: "ksht-cover.jpg", tags: [{ label: "Heritage" }, { label: "15-year partnership", cls: "ongoing" }], title: "KSHT — Kolkata Scottish Heritage Trust", desc: "A snake-infested cemetery became a biodiversity park with 70 species of flora and 53 species of fauna. Fifteen years of storytelling.", impact: "70 flora · 53 fauna species documented" },
  { slug: "case-study-recall-desert", img: "recall-desert-cover.jpg", tags: [{ label: "Citizen Science" }, { label: "Experience Design" }], title: "Recall — Desert Biodiversity", desc: "A four-hour sunset safari at Al Qudra Lakes. 25 people became field biologists. 50+ species documented. Field guides, bee stations, seed bombs.", impact: "50+ species documented in one afternoon" },
  { slug: "case-study-recall-shoreline", img: "recall-shoreline-cover.jpg", tags: [{ label: "Citizen Science" }, { label: "Research" }], title: "Recall — Shoreline Foraminifera Study", desc: "The first citizen science foraminifera study ever conducted in the UAE. Several taxa recorded on iNaturalist globally for the first time.", impact: "First global iNaturalist records — several taxa" },
  { slug: "case-study-chf-terracotta", img: "chf-terracotta-cover.jpg", tags: [{ label: "Heritage" }, { label: "Communication" }], title: "CHF — Terracotta Temples & Cemeteries", desc: "Two phases of Commonwealth Heritage Forum conservation work in Kolkata. Temples that appeared in Satyajit Ray's Pather Panchali, restored for the first time in India.", impact: "First-of-its-kind terracotta restoration in India" },
  { slug: "case-study-foret", img: "foret-cover.jpg", tags: [{ label: "Branding" }, { label: "Conservation" }], title: "Forêt", desc: "A brand identity built in mirror to its founder — Tania Das, landscape designer and forest ecology conservationist. The hawk moth in a circle.", impact: "A brand that carries the forest wherever it goes" },
  { slug: "case-study-amirs-laundry", img: "amir-laundry-cover.jpg", tags: [{ label: "Branding" }, { label: "Pro bono", cls: "probono" }], title: "Amir's Laundry", desc: "A pro bono rebrand for a Pakistani immigrant laundry in Dubai. The apostrophe in the logo is shaped like a leaf. A door hanger that was actually a manifesto.", impact: "The sustainable option costs nothing" },
  { slug: "case-study-ird", img: "ird-cover.jpg", tags: [{ label: "Social Media" }, { label: "Strategy" }], title: "IRD Studio — UDC Series", desc: "Full Q2 social media strategy for a Dubai Design District interior architecture studio. The Unwrapping Design Consciousness series.", impact: "Thought leadership built post by post" },
  { slug: "case-study-blueverse", img: "blueverse-cover.jpg", tags: [{ label: "Storytelling for a brand" }, { label: "Children's Book" }], title: "BlueVerse — A children's book for a car wash", desc: "BlueVerse, a water-efficient car care brand in the UAE, asked for a children's book — not a campaign. We made one: a watercolour storybook, three free colouring pages (one of their car wash), and animation scripts. The bridge between a litre saved and a sea preserved.", impact: "A car wash that teaches the next generation why water matters" },
  { slug: "case-study-canbridge", img: "canbridge-cover.jpg", tags: [{ label: "Awareness" }, { label: "Neurodiversity" }], title: "Canbridge", desc: "An autism centre for young adults in Chennai needed the world to see its community differently. Not awareness of limitation — celebration of uniqueness.", impact: "A reframe that changed the story people tell" },
  { slug: "case-study-desert-hive", img: "desert-hive-cover.jpeg", tags: [{ label: "Concept pitch", cls: "concept" }, { label: "Innovation" }], title: "Desert Hive Billboard", desc: "UAE's first regenerative billboard. A living insect hotel at billboard scale — supporting 40,000–60,000 native pollinators. Seeking investors.", impact: "A billboard that becomes habitat, not landfill" },
  { slug: "case-study-aurina-labs", img: "aurina-cover.jpg", tags: [{ label: "Concept Development", cls: "concept" }, { label: "Identity" }], title: "Aurina Labs", desc: "Lab-grown diamonds reframed as symbols of conscious power — not compromise, but conviction. What luxury looks like when it chooses the planet.", impact: "Luxury without extraction. Power without apology." },
  { slug: "case-study-magical-emotions", img: "magical-emotions-cover.jpg", tags: [{ label: "Children's Book" }, { label: "In Progress", cls: "ongoing" }], title: "Magical Emotions", desc: "A children's storybook guiding young readers through their emotional landscape — using Wizard Brain, whimsical emotion characters. Currently in production.", impact: "Every feeling has a name. Every child deserves to know it." },
];

const STORE_BEE = ["1767879514122.jpeg", "1767879513922.jpeg", "1767879513824.jpeg", "1767879513592.jpeg", "1767879513357.jpeg"];
const STORE_SKETCH = ["A5_Sketchbook_Inscape_Komorebi_5.jpg", "A5_Sketchbook_Inscape_Komorebi_4.jpg", "A5_Sketchbook_Inscape_Komorebi_6.jpg"];

export default function Home() {
  const [tw, setTw] = React.useState("");
  const [lb, setLb] = React.useState(null); // {images, index}
  const [beeIdx, setBeeIdx] = React.useState(0);
  const [skIdx, setSkIdx] = React.useState(0);
  const [enquire, setEnquire] = React.useState(null); // {item, kind} | null
  const location = useLocation();

  // typewriter effect
  React.useEffect(() => {
    let ti = 0, ci = 0, deleting = false, t;
    const tick = () => {
      const word = TITLES[ti];
      if (!deleting) {
        ci++;
        setTw(word.slice(0, ci));
        if (ci === word.length) { deleting = true; t = setTimeout(tick, PAUSE_AT_END_MS); return; }
      } else {
        ci--;
        setTw(word.slice(0, ci));
        if (ci === 0) { deleting = false; ti = (ti + 1) % TITLES.length; }
      }
      t = setTimeout(tick, deleting ? ERASE_INTERVAL_MS : TYPE_INTERVAL_MS);
    };
    tick();
    return () => clearTimeout(t);
  }, []);

  // smooth scroll on hash change
  React.useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), HASH_SCROLL_DELAY_MS);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location]);

  const openLb = (images, index = 0) => setLb({ images: images.map((i) => `/images/${i}`), index });
  const navLb = (dir) => setLb((s) => ({ ...s, index: (s.index + dir + s.images.length) % s.images.length }));

  const slideBee = (dir) => setBeeIdx((i) => (i + dir + STORE_BEE.length) % STORE_BEE.length);
  const slideSk = (dir) => setSkIdx((i) => (i + dir + STORE_SKETCH.length) % STORE_SKETCH.length);

  return (
    <div className="kmr-page home" data-testid="home-page">
      <Nav />

      <div className="hero">
        <video
          className="hero-video"
          src="/videos/komorebi-hero.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          data-testid="hero-video"
        />
        <div className="hero-video-overlay" aria-hidden="true" />
        <div className="hero-content">
          <p className="hero-eyebrow">A design studio with a conscience</p>
          <h1>Where <em>beauty</em> meets purpose,<br />the planet breathes.</h1>
          <p className="hero-sub">Komorebi is a multidisciplinary design studio working at the intersection of strategy, creativity, and systems change.</p>
          <div className="hero-cta">
            <button className="btn-primary" onClick={() => setEnquire({ item: "A first conversation", kind: "contact" })} data-testid="hero-cta-contact">Start a conversation</button>
            <Link to="/work" className="btn-outline" data-testid="hero-cta-work">See the work</Link>
          </div>
        </div>
      </div>

      <div className="marquee-wrap">
        <div className="marquee">
          {["a","b"].map((row) => (
            <React.Fragment key={row}>
              {["Strategy","Branding","Sustainability","Behaviour change","Citizen science","Education","Campaigns","Systems thinking"].map((w) => (
                <React.Fragment key={`${row}-${w}`}><span>{w}</span><span className="accent">&nbsp;·&nbsp;</span></React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      <section>
        <div className="container">
          <p className="section-label">What we do</p>
          <h2 className="section-title">Design that <em>repairs</em><br />as it creates</h2>
          <div className="services-grid">
            {[
              ["01","Strategy & consulting","Systems-level thinking that connects your purpose to real-world impact. We find the main thing and keep it the main thing."],
              ["02","Branding & identity","Visual identities that carry values in every pixel. Built to last, built to mean something."],
              ["03","Campaigns & storytelling","Communication that moves people from passive awareness to conscious action. Stories worth telling."],
              ["04","Sustainable design","Packaging, products, and experiences designed for the circular economy. Nothing leaves without a plan to return."],
            ].map(([n,name,desc]) => (
              <div key={n} className="service-card">
                <p className="service-num">{n}</p>
                <p className="service-name">{name}</p>
                <p className="service-desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="framework">
        <div className="container">
          <p className="section-label">Earth Consciousness™ Framework</p>
          <h2 className="section-title">How we <em>change</em> systems</h2>
          <div className="framework-grid">
            {[
              ["01","Break","Interrupt","The circular economy begins with interruption — not recycling. We find the pattern and disrupt it."],
              ["02","Seed","Plant","Consciousness doesn't arrive fully formed. We plant it — in a child's book, a bee station, a door hanger."],
              ["03","Rewire","Change","New behaviours need new infrastructure. We design the habit, not just the message."],
              ["04","Repeat","Systemise","The goal is not to win. It is to perpetuate the game. We build for permanence."],
            ].map(([n,name,t,d]) => (
              <div key={n} className="fw-step">
                <div className="fw-num">{n}</div>
                <p className="fw-name">{name}</p>
                <p className="fw-title">{t}</p>
                <p className="fw-desc">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section id="work">
        <div className="container">
          <p className="section-label">Selected work</p>
          <h2 className="section-title">Thirteen acts of <em>repair</em></h2>
          <div className="home-portfolio-grid">
            {PROJECTS.map((p, i) => (
              <Link to={`/work/${p.slug}`} key={p.slug} className="home-project-card" data-testid={`home-project-${i}`}>
                <div className="home-card-img"><img src={`/images/${p.img}`} alt={p.title} loading="lazy" /></div>
                <div className="home-card-body">
                  <div className="home-card-tags">
                    {p.tags.map((t) => <span key={t.label} className={`tag${t.cls ? " " + t.cls : ""}`}>{t.label}</span>)}
                  </div>
                  <p className="project-title">{p.title}</p>
                  <p className="project-desc">{p.desc}</p>
                  <p className="project-impact">{p.impact}</p>
                </div>
              </Link>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: "2.5rem" }}>
            <Link to="/work" className="btn-outline" data-testid="home-see-all-work">See all 13 projects →</Link>
          </div>
        </div>
      </section>

      <div className="manifesto">
        <div className="container">
          <blockquote>"I genuinely believe design can heal the planet if we create with purpose, intention and love. Nature is our ally in the matter."</blockquote>
          <cite>— Ishita Das, Founder</cite>
        </div>
      </div>

      <section id="about">
        <div className="container">
          <p className="section-label">About</p>
          <div className="about-grid">
            <div>
              <img src="/images/ishita.jpg" alt="Ishita Das" className="about-img" onClick={() => openLb(["ishita.jpg"], 0)} style={{ cursor: "zoom-in" }} data-testid="home-ishita-img" />
              <p className="about-caption">Sitting under The Great Wave. Of course.</p>
            </div>
            <div className="about-body">
              <div className="typewriter"><span>{tw}</span><span className="cursor"></span></div>
              <h2 className="section-title" style={{ fontSize: "2rem", marginBottom: "1.5rem" }}>Ishita Das</h2>
              <p>She was ten years old when she read an infographic in a nature magazine: plastic takes 500 years to decompose. Her analytical mind could not comprehend why anyone would choose to leave that much damage behind. <strong>That child is still running Komorebi.</strong></p>
              <p>Trained in Pune, Ahmedabad, and New York. A true Renaissance Designer — working across strategy, film, branding, education, citizen science, and conservation storytelling. Background across Ogilvy, human rights organisations, disaster documentation in the Sundarbans, and university teaching across three countries.</p>
              <p>She is not building a business. <strong>She is building a life that becomes a legacy.</strong></p>
              <p style={{ fontStyle: "italic", color: "var(--ink3)" }}>"Super fun to work with. I highly recommend it." — Ishita Das, on herself.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="store">
        <div className="container">
          <p className="section-label">The store</p>
          <h2 className="section-title">Objects with a <em>reason to exist</em></h2>

          {/* Bee Station — feature card */}
          <article className="bee-feature" data-testid="bee-feature">
            <div className="bee-feature-media" onClick={() => openLb(STORE_BEE, beeIdx)}>
              <img src={`/images/${STORE_BEE[beeIdx]}`} alt="Bee Station kit" />
              <button className="store-img-nav prev" onClick={(e) => { e.stopPropagation(); slideBee(-1); }} aria-label="Prev">‹</button>
              <button className="store-img-nav next" onClick={(e) => { e.stopPropagation(); slideBee(1); }} aria-label="Next">›</button>
              <div className="store-img-dots">
                {STORE_BEE.map((src, i) => <span key={src} className={`store-img-dot${i === beeIdx ? " active" : ""}`}></span>)}
              </div>
            </div>
            <div className="bee-feature-body">
              <p className="bee-feature-eyebrow">Featured · Citizen science object</p>
              <h3 className="bee-feature-title">Small action. <em>Vast ripple.</em></h3>
              <p className="bee-feature-name">Bee Station kit</p>
              <p className="bee-feature-desc">A small water source designed for bees, butterflies, and tiny pollinators to safely land and hydrate. Terracotta dish, pebbles, hand-illustrated instruction card. Designed by Komorebi as part of Recall — Desert Biodiversity. An act of care that keeps working long after it leaves our hands.</p>
              <div className="bee-feature-pillars">
                <div className="bee-pillar">
                  <p className="bee-pillar-label">The Object</p>
                  <p className="bee-pillar-text">A shallow terracotta dish with pebbles. Fill with water just enough to touch the stone tops. Place near plants. Refresh every 1–2 days. That's all. That's enough.</p>
                </div>
                <div className="bee-pillar">
                  <p className="bee-pillar-label">The Purpose</p>
                  <p className="bee-pillar-text">Bees, butterflies, and tiny pollinators need safe landing points to drink. The stones prevent drowning. Every sip matters.</p>
                </div>
                <div className="bee-pillar">
                  <p className="bee-pillar-label">The Ripple</p>
                  <p className="bee-pillar-text">30 stations distributed across Dubai homes from a single afternoon. Each one supports pollination — the invisible work behind our food, forests, and flowers.</p>
                </div>
              </div>
              <div className="bee-feature-footer">
                <span className="store-price">AED 35</span>
                <button className="btn-primary" onClick={() => setEnquire({ item: "Bee Station kit", kind: "enquire" })} data-testid="bee-feature-enquire">Enquire</button>
              </div>
            </div>
          </article>

          <div className="store-grid">
            {/* Tote */}
            <div className="store-card">
              <div className="store-img-wrap" onClick={() => openLb(["tote-bag.jpeg"], 0)}>
                <img src="/images/tote-bag.jpeg" alt="Tote bag" />
              </div>
              <div className="store-body">
                <p className="store-badge">Apparel</p>
                <p className="store-name">"I must be a good person" tote</p>
                <p className="store-desc">A joke and a manifesto at the same time. Canvas tote with the recycling symbol and the line that says everything: <em>I recycle.</em></p>
                <div className="store-footer">
                  <span className="store-price">AED 65</span>
                  <a className="store-btn" onClick={() => setEnquire({ item: "Tote bag", kind: "enquire" })} data-testid="store-tote-enquire" style={{ cursor: "pointer" }}>Enquire</a>
                </div>
              </div>
            </div>

            {/* Sketchbook */}
            <div className="store-card">
              <div className="store-img-wrap" onClick={() => openLb(STORE_SKETCH, skIdx)}>
                <img src={`/images/${STORE_SKETCH[skIdx]}`} alt="Inscape sketchbook" />
                <button className="store-img-nav prev" onClick={(e) => { e.stopPropagation(); slideSk(-1); }} aria-label="Prev">‹</button>
                <button className="store-img-nav next" onClick={(e) => { e.stopPropagation(); slideSk(1); }} aria-label="Next">›</button>
                <div className="store-img-dots">
                  {STORE_SKETCH.map((src, i) => <span key={src} className={`store-img-dot${i === skIdx ? " active" : ""}`}></span>)}
                </div>
              </div>
              <div className="store-body">
                <p className="store-badge">Physical product · Stationery</p>
                <p className="store-name">Inscape A5 sketchbook</p>
                <p className="store-desc">Kraft cover, exposed binding, thick recycled pages. The word INSCAPE is screenprinted in white. Designed to hold ideas worth keeping.</p>
                <div className="store-footer">
                  <span className="store-price">AED 85</span>
                  <a className="store-btn" onClick={() => setEnquire({ item: "Inscape A5 sketchbook", kind: "enquire" })} data-testid="store-sketch-enquire" style={{ cursor: "pointer" }}>Enquire</a>
                </div>
              </div>
            </div>

            {/* Seed bomb */}
            <div className="store-card">
              <div className="store-body" style={{ justifyContent: "center", background: "var(--sage-light)", minHeight: 200 }}>
                <p className="store-badge">Physical product</p>
                <p className="store-name">Seed bomb kit</p>
                <p className="store-desc">Native UAE wildflower seeds in a biodegradable casing. Throw them where they're needed. Each one is a small act of repair.</p>
                <div className="store-footer">
                  <span className="store-price">AED 28</span>
                  <a className="store-btn" onClick={() => setEnquire({ item: "Seed bomb kit", kind: "enquire" })} data-testid="store-seed-enquire" style={{ cursor: "pointer" }}>Enquire</a>
                </div>
              </div>
            </div>

            {/* Orange Peel Candle — made to order */}
            <div className="store-card">
              <div className="store-body" style={{ justifyContent: "center", background: "var(--paper3)", minHeight: 200 }}>
                <p className="store-badge">Made to order · Small batch</p>
                <p className="store-name">The Orange Peel Candle</p>
                <p className="store-desc"><em>Burns beautifully. Returns completely.</em> Natural soy wax inside a dried orange peel — the whole thing composts back when the week is up. 7-day burn. Only the small metal wick stand goes to recycling.</p>
                <div className="store-footer">
                  <span className="store-price">Enquire</span>
                  <a className="store-btn" onClick={() => setEnquire({ item: "Orange Peel Candle", kind: "enquire" })} data-testid="store-candle-enquire" style={{ cursor: "pointer" }}>Order yours</a>
                </div>
              </div>
            </div>

            {/* Winter Is Here — on enquiry */}
            <div className="store-card">
              <div className="store-body" style={{ justifyContent: "center", background: "var(--sage-light)", minHeight: 200 }}>
                <p className="store-badge">Apparel · On enquiry</p>
                <p className="store-name">Winter Is Here — snow jacket</p>
                <p className="store-desc">You can't deny climate change. Might as well wear it. A snow jacket with one line: <em>Winter is here.</em> Because if you're wearing it, the whole argument is on you.</p>
                <div className="store-footer">
                  <span className="store-price">On enquiry</span>
                  <a className="store-btn" onClick={() => setEnquire({ item: "Winter Is Here — snow jacket", kind: "enquire" })} data-testid="store-jacket-enquire" style={{ cursor: "pointer" }}>Enquire</a>
                </div>
              </div>
            </div>

            {/* Free downloads */}
            <div className="store-card">
              <div className="store-body" style={{ justifyContent: "center", background: "var(--paper3)", minHeight: 200 }}>
                <p className="store-badge">Free download · New</p>
                <p className="store-name">Baby On Board — the one people can actually see</p>
                <p className="store-desc"><em>Big enough to mean it.</em> Dubai traffic is not for the faint-hearted. A Baby On Board sticker designed to actually be noticed. Print it. Stick it. Pass it on.</p>
                <div className="store-footer">
                  <span className="store-price">Free</span>
                  <a className="store-btn free" href="/downloads/baby-on-board.pdf" download data-testid="store-bob-dl">Download &amp; print</a>
                </div>
              </div>
            </div>

            {/* Free downloads */}
            <div className="store-card">
              <div className="store-body" style={{ justifyContent: "center", background: "var(--paper3)", minHeight: 200 }}>
                <p className="store-badge">Free download</p>
                <p className="store-name">BlueVerse — Colouring pages</p>
                <p className="store-desc">"To let a dugong be." Three illustrated colouring pages from the BlueVerse storybook. Print, colour, share. For all ages.</p>
                <div className="store-footer">
                  <span className="store-price">Free</span>
                  <a className="store-btn free" href="/downloads/blueverse-colouring-pages.pdf" download data-testid="store-dumi-dl">Download now</a>
                </div>
              </div>
            </div>

            <div className="store-card">
              <div className="store-body" style={{ justifyContent: "center", background: "var(--sage-light)", minHeight: 200 }}>
                <p className="store-badge">Free download · New</p>
                <p className="store-name">BlueVerse — Dumi storybook</p>
                <p className="store-desc">"To let a dugong be." The full picture book about Dumi the Arabian dugong. A bedtime story that asks every child to look at the sea differently.</p>
                <div className="store-footer">
                  <span className="store-price">Free</span>
                  <a className="store-btn free" href="/downloads/blueverse-storybook.pdf" download data-testid="store-blueverse-dl">Download now</a>
                </div>
              </div>
            </div>

            <div className="store-card">
              <div className="store-body" style={{ justifyContent: "center", background: "var(--paper3)", minHeight: 200 }}>
                <p className="store-badge">Free download</p>
                <p className="store-name">UAE Desert field guide</p>
                <p className="store-desc">Species, ecosystems, and discovery prompts. Take it into the desert. Designed for citizen scientists and curious humans.</p>
                <div className="store-footer">
                  <span className="store-price">Free</span>
                  <a className="store-btn free" href="/downloads/uae-desert-field-guide.pdf" download data-testid="store-field-dl">Download now</a>
                </div>
              </div>
            </div>

            <div className="store-card">
              <div className="store-body" style={{ justifyContent: "center", background: "var(--sage-light)", minHeight: 200 }}>
                <p className="store-badge">Free download · New</p>
                <p className="store-name">Recall — Discovery survey & scorecard</p>
                <p className="store-desc">The full citizen-science discovery framework used at Recall events. Print, fill, score. The same tool 25 strangers used to become field biologists.</p>
                <div className="store-footer">
                  <span className="store-price">Free</span>
                  <a className="store-btn free" href="/downloads/recall-discovery-survey.pdf" download data-testid="store-discovery-dl">Download now</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="contact">
        <div className="container">
          <p className="section-label">Let's talk</p>
          <h2 className="section-title">The first conversation<br />is always <em>free</em></h2>
          <p style={{ color: "var(--ink2)", maxWidth: 440, margin: "0 auto 1rem", fontSize: "0.95rem" }}>We work with conscious brands, NGOs, social enterprises, and governments who need someone to connect the dots.</p>
          <a href="mailto:hey@komorebidesign.com" className="contact-email" data-testid="contact-email">hey@komorebidesign.com</a>
          <p style={{ fontSize: "0.85rem", color: "var(--ink2)", margin: "0.5rem auto 1.5rem" }}>or</p>
          <button className="btn-primary" onClick={() => setEnquire({ item: "A first conversation", kind: "contact" })} data-testid="contact-open-modal" style={{ marginBottom: "1.5rem" }}>Send us a note</button>
          <p style={{ fontSize: "0.8rem", color: "var(--ink3)", fontStyle: "italic" }}>Based in Dubai. Working globally.</p>
        </div>
      </section>

      <section className="locations" data-testid="locations-section">
        <div className="container" style={{ textAlign: "center" }}>
          <p className="section-label">Find us</p>
          <h2 className="section-title">Two cities, one <em>practice</em></h2>
        </div>
        <div className="locations-grid">
          <div className="location-card" data-testid="location-dubai">
            <iframe
              className="location-map"
              title="Komorebi Dubai"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=Majan%2C%20Dubai%2C%20UAE&output=embed"
            />
            <div className="location-body">
              <p className="location-label">Studio · UAE</p>
              <h3 className="location-name">Dubai</h3>
              <p className="location-addr">Majan, Dubai<br />United Arab Emirates</p>
            </div>
          </div>
          <div className="location-card" data-testid="location-kolkata">
            <iframe
              className="location-map"
              title="Komorebi Kolkata"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=Chak%20Garia%2C%20Kolkata%2C%20India&output=embed"
            />
            <div className="location-body">
              <p className="location-label">Roots · India</p>
              <h3 className="location-name">Kolkata</h3>
              <p className="location-addr">Chak Garia, Kolkata<br />West Bengal, India</p>
            </div>
          </div>
        </div>
      </section>

      <ClimateClock />
      <ChatWidget />
      <Footer />
      {lb && <Lightbox images={lb.images} index={lb.index} onClose={() => setLb(null)} onNav={navLb} />}
      <EnquireModal
        open={!!enquire}
        onClose={() => setEnquire(null)}
        item={enquire?.item || ""}
        kind={enquire?.kind || "enquire"}
      />
    </div>
  );
}
